import { QrCodeScanner, ForwardToInbox, Share } from "@mui/icons-material";
import { UnstyledButton } from "Components/Shared/Buttons/UnstyledButton";
import { Box, type ButtonProps, Stack } from "@mui/material";
import { Typography } from "@mui/material";
import { useState } from "react";
import { Resources, useResource } from "Translations/Resources";
import type { AffiliateUserRole } from "Types/types";
import {
  ContentDrawer,
  type DrawerState,
} from "Components/Affiliate/AffiliateActions/ContentDrawer";

const RESOURCES = Resources.Components.Affiliate;

type Props = {
  affiliateCodeUrl?: string;
  invitedUserRole: AffiliateUserRole;
};

export const AffiliateActionButtons = ({
  invitedUserRole,
  affiliateCodeUrl,
}: Props) => {
  const [drawerState, setDrawerState] = useState<DrawerState>("closed");
  const { t } = useResource();

  const handleShareClick = async () => {
    try {
      await navigator.share({
        title: t(RESOURCES.AffiliateLink.ShareButton.Title),
        text: t(RESOURCES.AffiliateLink.ShareButton.Text),
        url: affiliateCodeUrl,
      });
    } catch {} // error handling probably not necessary here
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="100%"
      gap={[0, 20]}
    >
      <ActionButton
        Icon={QrCodeScanner}
        label={t(RESOURCES.AffiliateLinkWithActions.QrButton)}
        onClick={() => setDrawerState("qrCode")}
        disabled={!affiliateCodeUrl}
      />
      <ActionButton
        Icon={ForwardToInbox}
        label={t(RESOURCES.AffiliateLinkWithActions.EmailButton)}
        onClick={() => setDrawerState("email")}
        disabled={!affiliateCodeUrl}
      />
      <ActionButton
        Icon={Share}
        label={t(RESOURCES.AffiliateLinkWithActions.ShareButton)}
        onClick={handleShareClick}
        disabled={!affiliateCodeUrl}
      />
      {affiliateCodeUrl && (
        <ContentDrawer
          drawerState={drawerState}
          onOpen={() => setDrawerState((prev) => prev)}
          onClose={() => setDrawerState("closed")}
          affiliateCode={affiliateCodeUrl}
          invitedUserRole={invitedUserRole}
        />
      )}
    </Box>
  );
};

// TODO: move to shared folder, unify with buttons in payment detail -----------------------------
interface ActionButtonProps extends ButtonProps {
  Icon: React.ElementType;
  label: string;
  onClick?: () => void;
}

const ActionButton = ({
  Icon,
  label,
  onClick,
  disabled,
  ...restProps
}: ActionButtonProps) => (
  <UnstyledButton
    aria-label={label}
    tabIndex={0}
    onClick={onClick}
    sx={{ justifyContent: "center", flexBasis: "33%" }}
    disabled={disabled}
    {...restProps}
  >
    <Stack gap={1.5} component="span" alignItems="center">
      <Icon
        sx={{
          fontSize: [32, 40],
          color: disabled ? "text.disabled" : "primary.main",
        }}
      />
      <Typography
        variant="body1"
        sx={{
          color: disabled ? "text.disabled" : "primary.main",
          fontWeight: 400,
          fontSize: [14, 16],
        }}
      >
        {label}
      </Typography>
    </Stack>
  </UnstyledButton>
);
//-------------------------------------------------------------------------------------------------
